import { LazyUndoRedoAction, UndoRedoAction } from "@/stores/undoRedoStore";
class CommentAction extends UndoRedoAction {
    constructor(store, comment) {
        super();
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "comment", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
        this.comment = structuredClone(comment);
    }
}
export class AddCommentAction extends CommentAction {
    get name() {
        return `add ${this.comment.type} comment`;
    }
    undo() {
        this.store.deleteComment(this.comment.id);
    }
    redo() {
        this.store.addComments([this.comment]);
    }
}
export class DeleteCommentAction extends CommentAction {
    get name() {
        return `delete ${this.comment.type} comment`;
    }
    run() {
        this.store.deleteComment(this.comment.id);
    }
    undo() {
        this.store.addComments([this.comment]);
    }
}
export class ChangeColorAction extends UndoRedoAction {
    constructor(store, comment, color) {
        super();
        Object.defineProperty(this, "commentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toColor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromColor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
        this.commentId = comment.id;
        this.fromColor = comment.color;
        this.toColor = color;
        this.type = comment.type;
    }
    get name() {
        return `change ${this.type} comment color to ${this.toColor}`;
    }
    run() {
        this.store.changeColor(this.commentId, this.toColor);
    }
    undo() {
        this.store.changeColor(this.commentId, this.fromColor);
    }
}
class LazyMutateCommentAction extends LazyUndoRedoAction {
    constructor(comment, key, data, applyDataCallback) {
        super();
        Object.defineProperty(this, "commentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "startData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "endData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "applyDataCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.commentId = comment.id;
        this.startData = structuredClone(comment[key]);
        this.endData = structuredClone(data);
        this.applyDataCallback = applyDataCallback;
        this.type = comment.type;
    }
    queued() {
        this.applyDataCallback(this.commentId, this.endData);
    }
    get name() {
        return `change ${this.type} comment`;
    }
    updateData(data) {
        this.endData = data;
        this.applyDataCallback(this.commentId, this.endData);
    }
    redo() {
        this.applyDataCallback(this.commentId, this.endData);
    }
    undo() {
        this.applyDataCallback(this.commentId, this.startData);
    }
}
export class LazyChangeDataAction extends LazyMutateCommentAction {
    constructor(store, comment, data) {
        const callback = store.changeData;
        super(comment, "data", data, callback);
    }
}
export class LazyChangePositionAction extends LazyMutateCommentAction {
    constructor(store, comment, position) {
        const callback = store.changePosition;
        super(comment, "position", position, callback);
    }
    get name() {
        return `change ${this.type} comment position`;
    }
}
export class LazyChangeSizeAction extends LazyMutateCommentAction {
    constructor(store, comment, size) {
        const callback = store.changeSize;
        super(comment, "size", size, callback);
    }
    get name() {
        return `resize ${this.type} comment`;
    }
}
export class ToggleCommentSelectedAction extends UndoRedoAction {
    constructor(store, comment) {
        super();
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "commentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toggleTo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
        this.commentId = comment.id;
        this.type = comment.type;
        this.toggleTo = !store.getCommentMultiSelected(this.commentId);
    }
    get name() {
        if (this.toggleTo === true) {
            return `add ${this.type} comment to selection`;
        }
        else {
            return `remove ${this.type} comment from selection`;
        }
    }
    run() {
        this.store.setCommentMultiSelected(this.commentId, this.toggleTo);
    }
    undo() {
        this.store.setCommentMultiSelected(this.commentId, !this.toggleTo);
    }
}
export class RemoveAllFreehandCommentsAction extends UndoRedoAction {
    constructor(store) {
        super();
        Object.defineProperty(this, "store", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "comments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.store = store;
        const freehandComments = store.comments.filter((comment) => comment.type === "freehand");
        this.comments = structuredClone(freehandComments);
    }
    get name() {
        return "remove all freehand comments";
    }
    run() {
        this.store.deleteFreehandComments();
    }
    undo() {
        this.store.addComments(structuredClone(this.comments));
    }
}
